import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { leagueService } from '../../services/leagueService';
import { isCompetitionPubliclyVisible } from '../../utils/publicCompetitionVisibility';

const formatDate = (isoDate) => {
  if (!isoDate) return 'N/A';
  return new Date(isoDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const statusBadgeClass = (status) => {
  if (status === 'active') return 'bg-success/10 text-success';
  if (status === 'upcoming') return 'bg-primary/10 text-primary';
  return 'bg-muted text-muted-foreground';
};

const LeagueDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadLeague();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadLeague = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await leagueService.getById(id);
      if (!isCompetitionPubliclyVisible(data)) {
        setError('This league is no longer available on the public portal.');
        setLeague(null);
        return;
      }
      setLeague(data);
      document.title = `${data?.name || 'League'} - Tanzania Basketball Federation`;
    } catch (err) {
      setError(err.message || 'Failed to load league');
    } finally {
      setIsLoading(false);
    }
  };

  const activeTeams = (league?.teams || []).filter((lt) => lt?.team);

  return (
    <PublicBrowseShell>
      <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={() => navigate('/leagues')} className="mb-6">
        Back to Leagues
      </Button>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading league...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : !league ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Trophy" size={40} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">League not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border card-shadow p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg bg-orange-600/10 flex items-center justify-center shrink-0">
                  <Icon name="Trophy" size={26} className="text-orange-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">{league?.name}</h1>
                  <p className="text-muted-foreground">{league?.season}</p>
                </div>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusBadgeClass(league?.leagueStatus)}`}>
                {league?.leagueStatus || 'upcoming'}
              </span>
            </div>
            <p className="text-muted-foreground mb-4">{league?.description || 'No description available.'}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-muted/30 rounded-lg p-3">
                <Icon name="CalendarDays" size={20} className="text-primary mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Start Date</p>
                <p className="font-medium text-foreground">{formatDate(league?.startDate)}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <Icon name="CalendarX" size={20} className="text-destructive mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">End Date</p>
                <p className="font-medium text-foreground">{formatDate(league?.endDate)}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <Icon name="Users" size={20} className="text-accent mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Teams</p>
                <p className="font-medium text-foreground">{league?.currentTeams ?? 0} / {league?.maxTeams ?? 0}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <Icon name="Award" size={20} className="text-success mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Prize Pool</p>
                <p className="font-medium text-foreground">{league?.prizePool ? `TZS ${league.prizePool}` : 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border card-shadow p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Participating Teams</h2>
            {activeTeams.length === 0 ? (
              <p className="text-muted-foreground text-sm">No teams have joined this league yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeTeams.map((lt) => (
                  <div key={lt.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
                      {lt?.team?.logoUrl ? (
                        <Image src={lt.team.logoUrl} alt={lt.team.name} className="w-full h-full object-cover" />
                      ) : (
                        <Icon name="Shield" size={18} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{lt?.team?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{lt?.team?.shortName}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {league?.standings?.length > 0 && (
            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Standings</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4">#</th>
                      <th className="py-2 pr-4">Team</th>
                      <th className="py-2 pr-4">GP</th>
                      <th className="py-2 pr-4">W</th>
                      <th className="py-2 pr-4">L</th>
                      <th className="py-2 pr-4">+/-</th>
                    </tr>
                  </thead>
                  <tbody>
                    {league.standings
                      .slice()
                      .sort((a, b) => (a.position || 0) - (b.position || 0))
                      .map((standing) => (
                        <tr key={standing.id} className="border-b border-border last:border-0">
                          <td className="py-2 pr-4 text-foreground">{standing?.position ?? '-'}</td>
                          <td className="py-2 pr-4 text-foreground font-medium">{standing?.team?.name || 'N/A'}</td>
                          <td className="py-2 pr-4 text-foreground">{standing?.gamesPlayed ?? 0}</td>
                          <td className="py-2 pr-4 text-foreground">{standing?.wins ?? 0}</td>
                          <td className="py-2 pr-4 text-foreground">{standing?.losses ?? 0}</td>
                          <td className="py-2 pr-4 text-foreground">{standing?.pointDifference ?? 0}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default LeagueDetailPage;
